import { ExternalAppId, ExternalAppStatus } from "../generated/enums"
import { omniaResolve } from "../promises"
import { RoleId } from "../user"
import { AppFiles, createFilesInterface } from "./appFiles"
import { AppMachines } from "./appMachines"
import { AppServiceImpl } from "./appService"
import { AppConnectionMode, IAppConnection, IAppDefinition, IAppOrganisation, IAppSession } from "./types"

/** @internal */
export class AppSession implements IAppSession {

    public connectionId: string
    public authorizeUri: string

    private connecting: Function

    private organisations: IAppOrganisation[]
    private defaultOrganisation: IAppOrganisation
    private config: {}

    public connected: boolean
    public status: ExternalAppStatus | -1

    public ownerId: number
    public ownerName: string

    private _connection: IAppConnection
    private files: AppFiles
    private machines: AppMachines

    /** This is only used for "virtual" applications such as iSoyl, which actually uses a Dropbox connection */
    private readonly _parentAppId: ExternalAppId

    constructor(
        public readonly app: IAppDefinition,
        private readonly svc: AppServiceImpl) {

        this._parentAppId = app.appId == ExternalAppId.iSoyl
            ? ExternalAppId.Dropbox
            : undefined

    }

    get appId() {
        return this.app.appId
    }

    get connection() {
        return this._connection
    }

    get oauth() {
        return this.app.connectionMode == AppConnectionMode.OAuth
    }

    get basicAuth() {
        return this.app.connectionMode == AppConnectionMode.BasicAuth
    }


    get hasOrganisations() {
        return this.organisations && this.organisations.length > 0
    }

    get orgRequired() {
        return this.app.orgs?.required
    }

    get orgLabel() {
        return this.app.orgs?.label
    }

    get orgMsg() {
        return this.app.orgs?.messages
    }

    get otherOwner() {

        if (!this.ownerId) return false

        const session = this.svc.session
        return session && (session.currentUser.Id != this.ownerId)

    }

    getConnection(refreshConfig?: boolean): ng.IPromise<IAppConnection> {

        const hasOrgs = !!this.app.orgs

        this._connection = undefined

        const appId = this._parentAppId || this.appId

        return this.svc.getConnection(appId)
            .then(cn => {

                this._connection = cn

                if (cn) {

                    this.connectionId = cn.connectionId
                    this.connected = true
                    this.status = cn.status
                    this.ownerId = cn.userId
                    this.ownerName = cn.userName

                    if (hasOrgs && (cn.status === 0) && (refreshConfig || (!this.organisations))) {
                        return this.refreshConfig().then(() => cn)
                    }

                    return cn
                }

            })
            .catch(() => {

                this.status = -1

                return this._connection

            })

    }

    refreshConfig() {

        return this.svc.getConfig(this.connectionId)
            .then(o => {

                this.organisations = o.organisations
                this.defaultOrganisation = o.organisations.find(org => org.id === o.org)
                this.config = o.config

                return this.organisations

            }).catch(() => {

                this.status = -1

            })

    }

    establishNewConnection(): ng.IPromise<any> {

        return this.svc.createConnection(this.app.appId).then(r => {
            if (r) {
                this.authorizeUri = r.authorizeUri
                this.connectionId = r.connectionId
            }
        })

    }

    onEstablishNewConnection(): void {

        if (!this.connecting) {

            this.connecting = this.svc.watchNewConnection(this.connectionId, () => {
                this.connected = true
                this.status = 0
                this.connecting = undefined
            })

        }

    }

    cancelConnection(): ng.IPromise<any> {

        if (this.connectionId) {
            this.cancelPolling()
            return this.svc.cancelConnection(this.connectionId)
        }

        return omniaResolve()
    }

    private cancelPolling() {
        if (this.connecting) {
            this.connecting()
            this.connecting = undefined
        }
    }

    getOrganisations() {
        return this.organisations
    }

    getDefaultOrganisation() {
        return this.defaultOrganisation
    }

    getConfig() {
        return this.config
    }

    saveConfig(org: { id: string, name: string }, config: {}): ng.IPromise<any> {

        const orgId = org?.id

        return this.svc
            .saveConfig(this.connectionId, org, config)
            .then(() => {

                this.config = { ...config }

                if (this.organisations) {
                    this.defaultOrganisation = orgId ? this.organisations.find(o => o.id === orgId) : null
                }

                this.svc.broadcastChange("orgs")

            })

    }

    getFiles() {
        return this.files = this.files ?? createFilesInterface(this, this.svc.files)
    }

    getMachines() {
        return this.machines = this.machines || new AppMachines(this.svc.machines, this)
    }

    disconnect(deleteData: boolean): ng.IPromise<any> {
        return this.svc.disconnect(this.connectionId, deleteData).then(r => {
            this.connected = false
            this.connectionId = null
            this.svc.broadcastChange()
        })
    }

    canUpdate() {

        if (!this.connected) return false

        // @ownerId is not set on new connections created locally, so that the owner is always
        // implicitly the current user in that case
        if (!this.ownerId) return true

        const session = this.svc.session
        const admin = (session.currentUserRole <= RoleId.Admin)
        return admin || (this.ownerId == session.currentUser.Id)

    }

    checkSyncData(): ng.IPromise<boolean> {
        if (!this.connected) return omniaResolve(false)
        if (!this.connectionId) return omniaResolve(false)
        return this.svc.checkSyncData(this.connectionId)
    }

}
